import { db } from "./db-client";
import { auth } from "./auth-client";

declare global {
  interface Window {
    OneSignal?: {
      init: (options: { appId: string }) => Promise<void>;
      logout: () => Promise<void>;
      User: {
        PushSubscription: {
          optedIn: boolean;
          id: string | null;
          addEventListener: (event: string, callback: (subscription: { current: { optedIn: boolean } }) => void) => void;
          optOut: () => Promise<void>;
        };
      };
      Notifications: {
        addEventListener: (event: string, callback: (event: { notification: { id: string; additionalData?: Record<string, unknown> } }) => void) => void;
      };
      Slidedown: {
        promptPushNotifications: (options?: { force?: boolean }) => Promise<void>;
      };
    };
  }
}

let oneSignalInitialized = false;

/**
 * Check if user is authenticated
 */

/** True when `url` is an https URL on this page's origin. */
function isFirstPartyOrigin(url: string): boolean {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

export const isUserAuthenticated = async (): Promise<boolean> => {
  const { data: { session } } = await auth.getSession();
  return !!session;
};

/**
 * Cleanup OneSignal state on logout
 * Deletes the player ID from the database and logs out from OneSignal SDK
 */
export const logoutOneSignal = async (): Promise<void> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    return;
  }

  try {
    // Get the current player ID before logging out
    const playerId = window.OneSignal.User?.PushSubscription?.id;

    // Delete player ID from database if we have one
    if (playerId) {
      try {
        const { data: { session } } = await auth.getSession();
        if (session?.user) {
          const { error } = await db.from("onesignal_player_ids")
            .delete()
            .eq("user_id", session.user.id)
            .eq("player_id", playerId);

          if (error) {
            console.warn("Failed to delete player ID from database:", error.message);
          }
        }
      } catch (dbError) {
        console.warn("Error deleting player ID from database:", dbError);
      }
    }

    // If newer OneSignal SDK with User model
    if (window.OneSignal.logout) {
      await window.OneSignal.logout();
    }
    // Also consider unregistering/opting out if requirements imply strict no-ghost-subscription,
    // but usually logout is enough to dissociate the user.
    // However, the device might still receive notifications if we don't opt out.
    // The requirement says: "optionally unsubscribe from OneSignal"
    if (window.OneSignal.User?.PushSubscription?.optedIn) {
      // We can't programmatically unsubscribe easily without user interaction in some browsers,
      // but OneSignal.User.PushSubscription.optOut() might work if available.
      // For now, let's just create the hook and stick to user request of "handle cleanup".
      // We will assume "logout" is sufficient to clear identity.
      // If we want to unsubscribe: window.OneSignal.User.PushSubscription.optOut();
      if ((window.OneSignal.User.PushSubscription as any).optOut) {
        (window.OneSignal.User.PushSubscription as any).optOut();
      }
    }
  } catch (error) {
    console.error("Error logging out from OneSignal:", error);
  }
};


/**
 * Reset OneSignal initialization state to allow retry
 */
export const resetOneSignalInitialization = (): void => {
  oneSignalInitialized = false;
};

/**
 * Error types for OneSignal operations
 */
export class OneSignalError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = "OneSignalError";
  }
}

/**
 * Permission status result
 */
export interface PermissionStatus {
  browser: NotificationPermission | "unsupported";
  oneSignal: boolean;
  canRequest: boolean;
}

/**
 * Check if current domain is allowed for OneSignal
 * This prevents domain restriction errors from appearing in console
 * 
 * OneSignal only works on an app subdomain, matching OneSignalContext behavior.
 * Allowed domains:
 * - app.lydertalk.com (production app subdomain)
 * - app.<env>.lydertalk.com (staging and preview deployments)
 * - *.localhost (local development)
 *
 * NOTE: Which hosts may actually subscribe is decided by the OneSignal
 * dashboard, not here — the SDK still enforces it during `init`. This check
 * only keeps our own marketing/admin hosts from even trying.
 */
const isDomainAllowed = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const currentHost = window.location.hostname;

  // Allow all localhost variants for local development
  // This includes: localhost, app.localhost, admin.localhost, www.localhost, etc.
  if (currentHost === "localhost" || currentHost.endsWith(".localhost") || currentHost.endsWith(".lydertalk.local")) {
    return true;
  }

  // `app.` on the production base or an environment base (app.stg…,
  // app.preview…). Root and admin hosts are deliberately excluded.
  return /^app\.(?:[a-z0-9-]+\.)*lydertalk\.com$/.test(currentHost);
};

/**
 * Parse domain restriction error message to extract allowed domains
 */
const parseAllowedDomainsFromError = (errorMessage: string): string[] => {
  // Format: "Can only be used on: http://localhost:8080"
  const match = errorMessage.match(/Can only be used on:\s*(.+)/i);
  if (match) {
    return match[1].split(',').map(d => d.trim());
  }
  return [];
};

/**
 * Get helpful error message for domain restriction errors
 */
const getDomainRestrictionHelpMessage = (currentHost: string, allowedDomains: string[]): string => {
  const isDevelopment = import.meta.env.DEV;
  const isHTTP = window.location.protocol === 'http:';

  // Check if this is the root domain vs app subdomain issue
  const isRootDomainConfigured = allowedDomains.some(domain =>
    domain.includes('lydertalk.com') && !domain.includes('app.lydertalk.com')
  );
  const isOnAppSubdomain = currentHost === 'app.lydertalk.com';

  let message = `OneSignal domain restriction: The current domain "${currentHost}" is not allowed.\n\n`;

  if (allowedDomains.length > 0) {
    message += `Allowed domains in OneSignal dashboard: ${allowedDomains.join(', ')}\n\n`;
  }

  // Provide specific instructions for root domain vs app subdomain issue
  if (isRootDomainConfigured && isOnAppSubdomain) {
    message += `⚠️ ROOT DOMAIN MISMATCH DETECTED ⚠️\n\n`;
    message += `OneSignal is configured for the root domain (https://lydertalk.com) but the app runs on the app subdomain (https://app.lydertalk.com).\n\n`;
    message += `To fix this:\n`;
    message += `1. Go to https://app.onesignal.com\n`;
    message += `2. Select your app\n`;
    message += `3. Navigate to Settings > Platforms > Web Push\n`;
    message += `4. Remove "https://lydertalk.com" from allowed domains\n`;
    message += `5. Add "https://app.lydertalk.com" to allowed domains (or enable "Allow all domains")\n`;
    message += `6. Save your changes\n`;
    message += `7. Clear browser cache and service workers, then reload\n\n`;
  } else {
    message += `To fix this:\n`;
    message += `1. Go to https://app.onesignal.com > Settings > Platforms > Web Push\n`;
    message += `2. Enable "Allow all domains" (recommended) OR add "${currentHost}" manually\n`;
    message += `3. Remove any HTTP URLs (OneSignal only supports HTTPS for new integrations)\n`;
    message += `4. Clear browser cache and service workers, then reload\n\n`;
  }

  if (isDevelopment) {
    message += `[DEV MODE] For local development:\n`;
    message += `- OneSignal requires HTTPS in production\n`;
    message += `- HTTP URLs like "http://localhost:8080" are not supported for new integrations\n`;
    message += `- Test on an HTTPS staging deployment instead\n`;
  }

  if (isHTTP) {
    message += `\n[WARNING] Your current URL uses HTTP. OneSignal requires HTTPS for push notifications.\n`;
  }

  return message;
};

/**
 * Initialize OneSignal SDK with error handling
 */
export const initializeOneSignal = async (appId: string): Promise<void> => {
  if (oneSignalInitialized || typeof window === "undefined" || !window.OneSignal) {
    return;
  }

  // Check domain before initializing to prevent console errors
  if (!isDomainAllowed()) {
    const currentHost = window.location.hostname;
    const helpMessage = getDomainRestrictionHelpMessage(currentHost, []);

    if (import.meta.env.DEV) {
      console.warn('[OneSignal] Domain not allowed for initialization:', currentHost);
      console.info('[OneSignal] To fix this issue:', helpMessage);
    }

    throw new OneSignalError(
      `OneSignal is not configured for domain: ${currentHost}. Please enable "Allow all domains" in your OneSignal dashboard or add this domain manually.`,
      "DOMAIN_RESTRICTED"
    );
  }

  // Validate SDK methods exist
  if (typeof window.OneSignal.init !== "function") {
    throw new OneSignalError(
      "OneSignal.init not available, SDK may not be fully loaded",
      "SDK_NOT_LOADED"
    );
  }

  try {
    // Initialize OneSignal
    await window.OneSignal.init({ appId });

    oneSignalInitialized = true;

    if (import.meta.env.DEV) {
      console.log('[OneSignal] Successfully initialized on:', window.location.hostname);
    }

    // Set up notification opened handler with validation
    if (window.OneSignal.Notifications && typeof window.OneSignal.Notifications.addEventListener === "function") {
      window.OneSignal.Notifications.addEventListener("click", (event) => {
        try {
          const additionalData = event.notification?.additionalData;
          const target = typeof additionalData?.url === "string" ? additionalData.url : "";
          // Same-origin path or first-party absolute URL only. A push payload is
          // server-written today, but this sink navigates the top-level window
          // and would execute a `javascript:` value if one ever reached it.
          if (target.startsWith("/") && !target.startsWith("//")) {
            window.location.href = target;
          } else if (/^https:\/\//i.test(target) && isFirstPartyOrigin(target)) {
            window.location.href = target;
          }
        } catch (error) {
          console.error("Error handling notification click:", error);
        }
      });
    }

    // Handle subscription changes with validation
    if (window.OneSignal.User?.PushSubscription && typeof window.OneSignal.User.PushSubscription.addEventListener === "function") {
      window.OneSignal.User.PushSubscription.addEventListener("change", async (subscription) => {
        try {
          if (subscription?.current?.optedIn) {
            await savePlayerId();
          }
        } catch (error) {
          console.error("Error handling subscription change:", error);
        }
      });

      // Check if already subscribed and save player ID
      try {
        if (window.OneSignal.User.PushSubscription.optedIn) {
          await savePlayerId();
        }
      } catch (error) {
        console.error("Error saving initial player ID:", error);
      }
    }
  } catch (error) {
    oneSignalInitialized = false;
    if (error instanceof OneSignalError) {
      throw error;
    }

    // Check if this is a domain restriction error
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes("Can only be used on")) {
      const currentHost = window.location.hostname;
      const allowedDomains = parseAllowedDomainsFromError(errorMessage);
      const helpMessage = getDomainRestrictionHelpMessage(currentHost, allowedDomains);

      // Check if OneSignal is configured for root domain but app is on subdomain
      const isRootDomainConfigured = allowedDomains.some(domain =>
        domain.includes('lydertalk.com') && !domain.includes('app.lydertalk.com')
      );
      const isOnAppSubdomain = currentHost === 'app.lydertalk.com';

      // Log helpful message in development mode
      if (import.meta.env.DEV) {
        console.warn('[OneSignal] Domain restriction error detected');
        console.info(helpMessage);
      }

      // Provide specific message for production domain mismatch
      let userMessage = `Domain restriction error: ${errorMessage}`;
      if (isRootDomainConfigured && isOnAppSubdomain) {
        userMessage += `\n\nOneSignal is configured for the root domain (https://lydertalk.com) but the app runs on the app subdomain (https://app.lydertalk.com).\n\n`;
        userMessage += `To fix this:\n`;
        userMessage += `1. Go to https://app.onesignal.com\n`;
        userMessage += `2. Select your app\n`;
        userMessage += `3. Navigate to Settings > Platforms > Web Push\n`;
        userMessage += `4. Remove "https://lydertalk.com" from allowed domains\n`;
        userMessage += `5. Add "https://app.lydertalk.com" to allowed domains (or enable "Allow all domains")\n`;
        userMessage += `6. Save your changes\n`;
        userMessage += `7. Clear browser cache and service workers, then reload\n`;
      } else {
        userMessage += import.meta.env.DEV
          ? '\n\nSee console for detailed fix instructions.'
          : '\n\nPlease configure OneSignal dashboard to allow this domain.';
      }

      // Domain restriction error - throw with enhanced message
      throw new OneSignalError(
        userMessage,
        "DOMAIN_RESTRICTED",
        error
      );
    }

    throw new OneSignalError(
      `Error initializing OneSignal: ${errorMessage}`,
      "INIT_ERROR",
      error
    );
  }
};

/**
 * Check browser notification permission status
 */
export const getBrowserNotificationPermission = (): NotificationPermission | "unsupported" => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
};

/**
 * Check current permission status (browser + OneSignal)
 */
export const getPermissionStatus = (): PermissionStatus => {
  const browser = getBrowserNotificationPermission();
  const oneSignal = isOneSignalAvailable() &&
    window.OneSignal?.User?.PushSubscription?.optedIn === true;
  const canRequest = browser === "default" || browser === "unsupported";

  return {
    browser,
    oneSignal,
    canRequest,
  };
};

/**
 * Request browser notification permission directly (native API)
 */
export const requestBrowserNotificationPermission = async (): Promise<NotificationPermission> => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    throw new OneSignalError("Notifications not supported in this browser", "NOT_SUPPORTED");
  }

  // If already granted or denied, return current state
  if (Notification.permission !== "default") {
    return Notification.permission;
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * A browser permission grant is not a push subscription: with no OneSignal
 * subscription there is no player id to store, so the device would receive
 * nothing while the UI reported success.
 */
const resultForNativeGrant = async (): Promise<{ success: boolean; error?: OneSignalError }> => {
  if (!window.OneSignal?.User?.PushSubscription?.optedIn) {
    return {
      success: false,
      error: new OneSignalError(
        "Push notifications are not available on this device.",
        "SUBSCRIPTION_NOT_AVAILABLE"
      ),
    };
  }

  try {
    await savePlayerId();
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof OneSignalError
          ? error
          : new OneSignalError("Failed to save the push subscription", "SAVE_ERROR", error),
    };
  }
};

/**
 * Request push notification permission with proper error handling
 * Falls back to native browser permission request if OneSignal is not available
 */
export const requestNotificationPermission = async (): Promise<{ success: boolean; error?: OneSignalError }> => {
  if (typeof window === "undefined") {
    return {
      success: false,
      error: new OneSignalError("Window not available", "NOT_AVAILABLE"),
    };
  }

  // Check authentication first
  const isAuthenticated = await isUserAuthenticated();
  if (!isAuthenticated) {
    return {
      success: false,
      error: new OneSignalError(
        "You must be signed in to enable notifications.",
        "NOT_AUTHENTICATED"
      ),
    };
  }

  // Check browser permission first
  const browserPermission = getBrowserNotificationPermission();
  if (browserPermission === "denied") {
    return {
      success: false,
      error: new OneSignalError(
        "Browser notification permission was denied. Please enable it in your browser settings.",
        "BROWSER_PERMISSION_DENIED"
      ),
    };
  }

  // If OneSignal is not available, fall back to native browser permission request
  if (!window.OneSignal) {
    try {
      const permission = await requestBrowserNotificationPermission();
      if (permission === "granted") {
        return await resultForNativeGrant();
      } else if (permission === "denied") {
        return {
          success: false,
          error: new OneSignalError(
            "Notification permission was denied. Please enable it in your browser settings.",
            "PERMISSION_DENIED"
          ),
        };
      } else {
        return {
          success: false,
          error: new OneSignalError(
            "Permission request was dismissed",
            "PERMISSION_NOT_GRANTED"
          ),
        };
      }
    } catch (error) {
      return {
        success: false,
        error: new OneSignalError(
          `Error requesting browser notification permission: ${error instanceof Error ? error.message : "Unknown error"}`,
          "REQUEST_ERROR",
          error
        ),
      };
    }
  }

  // Validate SDK methods exist - if not, fall back to browser permission
  if (!window.OneSignal.Slidedown || typeof window.OneSignal.Slidedown.promptPushNotifications !== "function") {
    try {
      const permission = await requestBrowserNotificationPermission();
      if (permission === "granted") {
        return await resultForNativeGrant();
      } else if (permission === "denied") {
        return {
          success: false,
          error: new OneSignalError(
            "Notification permission was denied. Please enable it in your browser settings.",
            "PERMISSION_DENIED"
          ),
        };
      } else {
        return {
          success: false,
          error: new OneSignalError(
            "Permission request was dismissed",
            "PERMISSION_NOT_GRANTED"
          ),
        };
      }
    } catch (error) {
      return {
        success: false,
        error: new OneSignalError("OneSignal Slidedown not available", "SLIDEDOWN_NOT_AVAILABLE"),
      };
    }
  }

  if (!window.OneSignal.User?.PushSubscription) {
    // Fall back to browser permission
    try {
      const permission = await requestBrowserNotificationPermission();
      if (permission === "granted") {
        return await resultForNativeGrant();
      } else if (permission === "denied") {
        return {
          success: false,
          error: new OneSignalError(
            "Notification permission was denied. Please enable it in your browser settings.",
            "PERMISSION_DENIED"
          ),
        };
      } else {
        return {
          success: false,
          error: new OneSignalError(
            "Permission request was dismissed",
            "PERMISSION_NOT_GRANTED"
          ),
        };
      }
    } catch (error) {
      return {
        success: false,
        error: new OneSignalError("OneSignal PushSubscription not available", "SUBSCRIPTION_NOT_AVAILABLE"),
      };
    }
  }

  try {
    // Check if already subscribed
    if (window.OneSignal.User.PushSubscription.optedIn) {
      await savePlayerId();
      return { success: true };
    }

    // Show the OneSignal prompt
    await window.OneSignal.Slidedown.promptPushNotifications();

    // Wait a bit for the user to respond, then check again
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (window.OneSignal.User.PushSubscription.optedIn) {
      await savePlayerId();
      return { success: true };
    }

    // Check if permission was denied
    const currentBrowserPermission = getBrowserNotificationPermission();
    if (currentBrowserPermission === "denied") {
      return {
        success: false,
        error: new OneSignalError(
          "Notification permission was denied",
          "PERMISSION_DENIED"
        ),
      };
    }

    return {
      success: false,
      error: new OneSignalError(
        "Permission request was not granted",
        "PERMISSION_NOT_GRANTED"
      ),
    };
  } catch (error) {
    if (error instanceof OneSignalError) {
      return { success: false, error };
    }
    return {
      success: false,
      error: new OneSignalError(
        `Error requesting notification permission: ${error instanceof Error ? error.message : "Unknown error"}`,
        "REQUEST_ERROR",
        error
      ),
    };
  }
};

/**
 * Save OneSignal player ID to database
 */
const savePlayerId = async (): Promise<void> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    throw new OneSignalError("OneSignal not available", "NOT_AVAILABLE");
  }

  const playerId = window.OneSignal.User?.PushSubscription?.id;
  if (!playerId) {
    throw new OneSignalError("Player ID not available", "NO_PLAYER_ID");
  }

  const {
    data: { session },
    error: sessionError,
  } = await auth.getSession();

  if (sessionError) {
    throw new OneSignalError(
      `Failed to get session: ${sessionError.message}`,
      "SESSION_ERROR",
      sessionError
    );
  }

  if (!session?.user) {
    throw new OneSignalError("User not authenticated", "NOT_AUTHENTICATED");
  }

  // Upsert player ID
  const { error } = await db.from("onesignal_player_ids")
    .upsert(
      {
        user_id: session.user.id,
        player_id: playerId,
      },
      {
        onConflict: "user_id,player_id",
      }
    );

  if (error) {
    throw new OneSignalError(
      `Failed to save player ID: ${error.message}`,
      "SAVE_ERROR",
      error
    );
  }
};

/**
 * Get current user's OneSignal player IDs with error handling
 */
export const getUserPlayerIds = async (userId: string): Promise<{ playerIds: string[]; error?: OneSignalError }> => {
  if (!userId) {
    return {
      playerIds: [],
      error: new OneSignalError("User ID is required", "INVALID_USER_ID"),
    };
  }

  try {
    const { data, error } = await db.from("onesignal_player_ids")
      .select("player_id")
      .eq("user_id", userId);

    if (error) {
      return {
        playerIds: [],
        error: new OneSignalError(
          `Error fetching player IDs: ${error.message}`,
          "FETCH_ERROR",
          error
        ),
      };
    }

    return {
      playerIds: data?.map((row) => row.player_id) || [],
    };
  } catch (error) {
    if (error instanceof OneSignalError) {
      return { playerIds: [], error };
    }
    return {
      playerIds: [],
      error: new OneSignalError(
        `Error in getUserPlayerIds: ${error instanceof Error ? error.message : "Unknown error"}`,
        "UNKNOWN_ERROR",
        error
      ),
    };
  }
};

/**
 * Check if OneSignal is available
 */
export const isOneSignalAvailable = (): boolean => {
  return typeof window !== "undefined" && typeof window.OneSignal !== "undefined";
};

